// Perfiles (maestro) sobre TanStack Query. Mismo patrón que `useUsuarios`: la
// cache de la query es la única fuente de verdad del listado y las mutaciones
// invalidan esa key en vez de parchear la lista a mano.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  actualizarPerfilRequest,
  crearPerfilRequest,
  eliminarPerfilRequest,
  fetchPerfiles,
  type PerfilInput,
} from "@/lib/perfiles";

export const PERFILES_KEY = ["perfiles"] as const;

export function usePerfiles() {
  return useQuery({
    queryKey: PERFILES_KEY,
    queryFn: fetchPerfiles,
  });
}

export function useCrearPerfil() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PerfilInput) => crearPerfilRequest(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PERFILES_KEY }),
  });
}

export function useActualizarPerfil() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: PerfilInput & { id: number }) =>
      actualizarPerfilRequest(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PERFILES_KEY }),
  });
}

interface OpcionesDeBorrado {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

/**
 * Los callbacks van acá (nivel `useMutation`) y NO en el `mutate(id, {...})`
 * del componente A PROPÓSITO.
 *
 * TanStack Query descarta los callbacks pasados a `mutate()` si el componente
 * que los registró se desmonta antes de que la mutación termine — y el
 * diálogo de borrado se desmonta al cerrarse (lo remonta el `key` del perfil).
 * Con el manejo ahí, cerrar el diálogo con el DELETE en vuelo hacía que el
 * rechazo del backend no produjera NINGUNA señal: ni alerta, ni toast, ni
 * recarga del listado. Los de `useMutation` corren igual.
 */
export function useEliminarPerfil(opciones: OpcionesDeBorrado = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => eliminarPerfilRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PERFILES_KEY });
      opciones.onSuccess?.();
    },
    onError: (error) => opciones.onError?.(error),
  });
}
